import React, { useState } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import {
  Container, Card, CardContent, TextField, Button,
  Typography, Alert, CircularProgress, Box, Dialog,
  DialogTitle, DialogContent, DialogActions, Link
} from '@mui/material';
import { Login as LoginIcon, Email, LockReset } from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';

const API_BASE = API_BASE_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Activation modal state
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [activating, setActivating] = useState(false);

  // Password reset modal state
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email,
        password
      });

      const data = response.data;

      // Login successful - update context
      login({
        uid: data.uid,
        email: data.email,
        role: data.role,
        idToken: data.idToken,
        refreshToken: data.refreshToken
      });

      // Navigate based on role
      if (data.role === 'Admin') {
        navigate('/admin/add-requests');
      } else {
        navigate('/');
      }

    } catch (err) {
      const errorData = err.response?.data;

      // Check if error is EMAIL_VERIFICATION_REQUIRED
      if (errorData?.error === 'EMAIL_VERIFICATION_REQUIRED') {
        // User needs to activate account
        setActivationModalOpen(true);
        setLoading(false);
        return;
      }

      // Other errors
      setError(errorData?.message || errorData?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setError('');
    setActivating(true);

    try {
      await axios.post(`${API_BASE}/auth/send-activation-code`, {
        email
      });

      setCodeSent(true);
      alert('Activation code sent! Check your email.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send activation code');
    } finally {
      setActivating(false);
    }
  };

  const handleActivate = async () => {
    if (!activationCode || activationCode.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setError('');
    setActivating(true);

    try {
      await axios.post(`${API_BASE}/auth/activate-account`, {
        email,
        code: activationCode
      });

      // Account activated! Close modal and try login again
      setActivationModalOpen(false);
      setActivationCode('');
      setCodeSent(false);

      alert('Account activated! Logging you in...');

      // Automatically login now
      handleLogin({ preventDefault: () => {} });

    } catch (err) {
      setError(err.response?.data?.error || 'Activation failed');
    } finally {
      setActivating(false);
    }
  };

  const handleOpenResetModal = () => {
    setResetEmail(email); // Pre-fill with login email if provided
    setResetModalOpen(true);
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      alert('Please enter your email address');
      return;
    }

    setResetting(true);

    try {
      await axios.post(`${API_BASE}/auth/request-password-reset`, {
        email: resetEmail
      });

      alert('If this email exists in our system, a temporary password has been sent. Check your email and use it to login. Note: You will still need to activate your account.');
      setResetModalOpen(false);
      setResetEmail('');
    } catch (err) {
      alert('Failed to process password reset');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LoginIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h4" gutterBottom>
              Alumni Login
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 1 }}
            />

            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleOpenResetModal}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Activation Modal */}
      <Dialog
        open={activationModalOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            <Typography variant="h6">
              Activate Your Account
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Your account has been approved but needs activation. We'll send a 6-digit code to your email.
          </Alert>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Email: <strong>{email}</strong>
          </Typography>

          {!codeSent ? (
            <Typography variant="body2" color="text.secondary">
              Click "Send Code" to receive your activation code via email.
            </Typography>
          ) : (
            <>
              <TextField
                fullWidth
                label="Activation Code"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputProps={{
                  maxLength: 6,
                  style: { fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }
                }}
                sx={{ mb: 2 }}
              />

              <Button
                variant="text"
                size="small"
                onClick={handleSendCode}
                disabled={activating}
              >
                Didn't receive code? Resend
              </Button>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setActivationModalOpen(false);
              setActivationCode('');
              setCodeSent(false);
              setError('');
            }}
            disabled={activating}
          >
            Cancel
          </Button>

          {!codeSent ? (
            <Button
              variant="contained"
              onClick={handleSendCode}
              disabled={activating}
              startIcon={activating && <CircularProgress size={20} />}
            >
              {activating ? 'Sending...' : 'Send Code'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleActivate}
              disabled={activating || activationCode.length !== 6}
              startIcon={activating && <CircularProgress size={20} />}
            >
              {activating ? 'Activating...' : 'Activate'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog
        open={resetModalOpen}
        onClose={() => !resetting && setResetModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockReset color="primary" />
            <Typography variant="h6">
              Reset Password
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            A temporary password will be sent to your email. You'll still need to activate your account after logging in with the temporary password.
          </Alert>

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            placeholder="Enter your email"
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary">
            For security, we'll send a temporary password to your email. This prevents unauthorized password resets.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setResetModalOpen(false);
              setResetEmail('');
            }}
            disabled={resetting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handlePasswordReset}
            disabled={resetting || !resetEmail}
            startIcon={resetting && <CircularProgress size={20} />}
          >
            {resetting ? 'Sending...' : 'Send Temporary Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginPage;